import * as React from "react";
import * as ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, Route, redirect } from 'react-router-dom';
import "./index.css";
import App from "./App";
import Assets, { loader as assetLoader } from "./pages/Assets/Assets";
import Inspections, {loader as inspectionsLoader} from "./pages/Inspections/Inspections"
import AssetInfo, { loader as assetInfoLoader } from "./pages/Assets/AssetInfo";
import AssetAnalysisValidations , {loader as analysisValidationsLoader} from "./pages/Assets/AssetAnalysisValidations";
import Login, { loader as loginLoader } from "./pages/Login/Login";
import UserAdd from "./pages/UserAdd/UserAdd"
import ErrorPage from './error-page'
import { defaultApi } from "./api-interface";
import Users, {loader as userLoader } from "./pages/Users/Users";
import {loader as tenantDashboardLoader} from "./pages/Dashboard/Dashboard";
import UserEdit from "./pages/UserEdit/UserEdit";
import Register, {loader as registerLoader} from "./pages/Register/Register";
import PasswordReset, {loader as resetLoader} from "./pages/PasswordReset/PasswordReset";
import { ThemeProvider, createTheme } from "@mui/material";
import AssetAdd from "./pages/Assets/AssetAdd";
import Dashboard from "./pages/Dashboard/Dashboard";
import Contractors, {loader as contractorsLoader} from "./pages/Contractors/Contractors";
import ContractorAdd from "./pages/Contractors/ContractorAdd";
import ContractorsInfo, {loader as contractorsInfoLoader} from "./pages/Contractors/ContractorInfo";
import { Tenant } from "./api";
import Profile, {loader as profileLoader} from "./pages/Profile/Profile";

export const TenantContext = React.createContext<Tenant | null>(null);

async function dashboardTenantLoader(request) {
  try{
    const user = await defaultApi.readUsersMeMeGet()
    const destination = new URL(request.url).pathname
    if (destination == "/dashboard" || destination == "/dashboard/"){
      return redirect("/dashboard/"+user.data.tenants[0].id)
    } else {
      return null;
    }
  } catch {
    return redirect("/login?next="+new URL(request.url).pathname)
  }
}

async function dashboardLoader(request, setTenant, tenant_id) {
  try{
    const user = await defaultApi.readUsersMeMeGet()
    setTenant(tenant_id)
    return user.data;
  } catch {
    return redirect("/login?next="+new URL(request.url).pathname)
  }
}

async function rootLoader(request){
  const destination = new URL(request.url).pathname
  if (destination == "/"){
    return redirect("/dashboard")
  }
}

const CustomRouterProvider = () => {
  const [tenant, setTenant] = React.useState<Tenant | null>(null)
  const router = createBrowserRouter([
    {
      path: "/",
      loader: ({ request, params }) => rootLoader(request),
      errorElement: <ErrorPage />,
    },
    {
      path: "dashboard",
      loader: ({ request, params }) => dashboardTenantLoader(request),
      errorElement: <ErrorPage />,
    },
    {
      path: "dashboard/:tenant_id",
      loader: ({ request, params }) => dashboardLoader(request, setTenant, params.tenant_id),
      element: <App />,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          loader: async ({request, params}) => {
            const destination = new URL(request.url).pathname
            if (destination == "/dashboard/"+params.tenant_id || destination == "/dashboard/"+params.tenant_id+"/"){
              return redirect("/dashboard/"+params.tenant_id+"/dashboard")
            } else {
              return null;
            }
          },
       },
       {
        path: "dashboard",
        element: <Dashboard />,
        loader: ({request, params}) => tenantDashboardLoader(params.tenant_id),
        errorElement: <ErrorPage />,
       },
        {
          path: "users",
          element: <Users />,
          loader: ({request, params}) => userLoader(params.tenant_id),
          errorElement: <ErrorPage />,
        },
        {
          path: "users/add",
          element: <UserAdd/>,
          errorElement: <ErrorPage/>
        },
        {
          path: "users/:user_id",
          element: <UserEdit/>,
          loader: ({request, params}) => userLoader(params.tenant_id),
          errorElement: <ErrorPage/>
        },
        {
          path: "profile",
          element: <Profile/>,
          loader: ({request, params}) => profileLoader(params.tenant_id),
          errorElement: <ErrorPage/>
        },
        {
          path: "assets",
          loader: ({request, params}) => assetLoader(params.tenant_id),
          element: <Assets />,
          errorElement: <ErrorPage />,
        },
        {
          path: "assets/:asset_id",
          loader: ({params}) => assetInfoLoader(params.tenant_id, params.asset_id),
          element: <AssetInfo />,
          errorElement: <ErrorPage />,
        },
        {
          path: "assets/add",
          element: <AssetAdd/>,
          errorElement: <ErrorPage/>
        },
        {
          path: "inspections",
          loader: ({request, params}) => inspectionsLoader(params.tenant_id),
          element: <Inspections />,
          errorElement: <ErrorPage />,
        },
        {
          path: "inspections/:analysis_id/validations",
          loader: ({params}) => analysisValidationsLoader(params.tenant_id, params.analysis_id),
          element: <AssetAnalysisValidations />,
          errorElement: <ErrorPage />,
        },
        {
          path: "contractors",
          loader: ({request, params}) => contractorsLoader(params.tenant_id),
          element: <Contractors />,
          errorElement: <ErrorPage />,
        },
        {
          path: "contractors/add",
          element: <ContractorAdd />,
          errorElement: <ErrorPage />,
        },
        {
          path: "contractors/:contractor_id",
          loader: ({params}) => contractorsInfoLoader(params.tenant_id, params.contractor_id),
          element: <ContractorsInfo />,
          errorElement: <ErrorPage />,
        }
      ]
    },
    {
      path: "login",
      loader: loginLoader,
      element: <Login />,
      errorElement: <ErrorPage />,
    },
    {
      path: "register/:register_id/:register_secret",
      loader: ({ request, params }) => registerLoader(params.register_id, params.register_secret),
      element: <Register />,
      errorElement: <ErrorPage />,
    },
    {
      path: "password_reset/:reset_id/:reset_secret",
      loader: ({ request, params }) => resetLoader(params.reset_id, params.reset_secret),
      element: <PasswordReset />,
      errorElement: <ErrorPage />,
    }
  ]);
  
 
  return (
    <TenantContext.Provider value={tenant}>
      <RouterProvider router={router} />
    </TenantContext.Provider>
  );
 }

 declare module '@mui/material/styles' {
  interface Palette {
    successOutline: Palette['primary'];
    successBackground: Palette['primary'];
    warningBackground: Palette['primary'];
    warningOutline: Palette['primary'];
    errorOutline: Palette['primary'];
    errorBackground: Palette['primary'];
    pastellePurple: Palette['primary'];
    pastelleOrange: Palette['primary'];
  }

  interface PaletteOptions {
    successOutline?: PaletteOptions['primary'];
    successBackground?: PaletteOptions['primary'];
    warningBackground?: PaletteOptions['primary'];
    warningOutline?: PaletteOptions['primary'];
    errorOutline?: PaletteOptions['primary'];
    errorBackground?: PaletteOptions['primary'];
    pastellePurple?: PaletteOptions['primary'];
    pastelleOrange?: PaletteOptions['primary'];
  }
}



const theme = createTheme({
  typography: {
    h4:{
      fontWeight: "bold",
      fontSize: "2rem"
    }
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#202f4c',
      light: '#a0b8e6',
      dark: '#202f4c',
      contrastText: '#fff',
    },
    secondary: {
      main: '#43a047',
    },
    success: {
      main: "rgb(21 128 61 / var(--tw-text-opacity, 1))",
    },
    successOutline: {
      main: "rgb(74 222 128 / var(--tw-border-opacity, 1))",
    },
    successBackground: {
      main: "rgb(220 252 231 / var(--tw-bg-opacity, 1))",
    },
    error:{
      main: "rgb(185 28 28 / var(--tw-text-opacity, 1))",
    },
    errorBackground: {
      main: "rgb(253 236 234 / var(--tw-bg-opacity, 1))",
    },
    errorOutline: {
      main: "rgb(220 38 38 / var(--tw-border-opacity, 1))"
    },
    warning: {
      main: '#f59e0b',
    },
    warningBackground: {
      main: '#fef3c7',
    },
    warningOutline: {
      main: '#fdc200',
    },
    background: {
      default: '#f3f4f6',
      paper: '#f3f4f6',
    },
    pastellePurple: {
      main: '#d6c9f0',
    },
    pastelleOrange : {
      main: '#f9e2d2',
    }
  },
});


ReactDOM.createRoot(document.getElementById("root")!).render(
  <ThemeProvider theme={theme}>
    <React.StrictMode>
      <CustomRouterProvider/>
    </React.StrictMode>
  </ThemeProvider>
);